import { useState } from 'react';

import { isNil } from 'lodash';

import { AngleDown, AngleUp } from '@assets/icons';
import ProgressBar from '@components/progress-bar/ProgressBar';
import { useBulkWorkspaceCreationProgress } from '@hooks/use-bulk-workspace-creation-progress';
import { useUploadProgress } from '@hooks/use-upload-progress';

export enum UploadProgressType {
  BULK_WORKSPACE_CREATION = 'bulk_workspace_creation',
  ASSET_UPLOAD = 'asset_upload',
}

type ProgressCardProps = {
  type: UploadProgressType;
  items: Array<{ name: string; progressPercentage?: number }>;
  isOpen: boolean;
  onToggle: () => void;
};

const ProgressCard = ({ type, items, isOpen, onToggle }: ProgressCardProps) => {
  const isBulkCreation = type === UploadProgressType.BULK_WORKSPACE_CREATION;
  const AngleIcon = isOpen ? AngleDown : AngleUp;
  const title = isBulkCreation
    ? 'Bulk Workspace Creation Progress'
    : `Uploading ${items.length} item${items.length > 1 ? 's' : ''}`;

  return (
    <div className="flex flex-col bg-white shadow-md rounded-md w-64 max-h-[400px]">
      <div className="flex justify-between items-center px-5 py-3">
        <div className="font-semibold">{title}</div>
        <AngleIcon className="text-gray-3 cursor-pointer" onClick={onToggle} />
      </div>
      {isOpen && (
        <div className="flex-grow overflow-y-auto">
          {items.map(item => (
            <div key={item.name} className="border-t border-gray-2">
              <div className="flex justify-between items-center px-5 py-3">
                <div className="space-y-2 w-full">
                  {!isBulkCreation && (
                    <p className="min-w-0 break-words">{item.name}</p>
                  )}

                  {!isNil(item.progressPercentage) && (
                    <>
                      <ProgressBar progress={item.progressPercentage} />
                      <p className="text-gray-3 text-sm">
                        {item.progressPercentage === 100
                          ? 'Done!'
                          : `${item.progressPercentage}%`}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const UploadProgress = () => {
  const { items: assetUploadItems } = useUploadProgress();
  const { items: bulkWorkspaceCreationItems } =
    useBulkWorkspaceCreationProgress();

  const [isAssetUploadOpen, setIsAssetUploadOpen] = useState(true);
  const [isBulkCreationOpen, setIsBulkCreationOpen] = useState(true);

  const hasAssetUploads = assetUploadItems.length > 0;
  const hasBulkWorkspaceCreation = bulkWorkspaceCreationItems.length > 0;

  // Return null if no uploads are active
  if (!hasAssetUploads && !hasBulkWorkspaceCreation) return null;

  return (
    <div className="fixed bottom-0 right-0 p-5 mb-14 z-[100] flex flex-col gap-3">
      {hasBulkWorkspaceCreation && (
        <ProgressCard
          type={UploadProgressType.BULK_WORKSPACE_CREATION}
          items={bulkWorkspaceCreationItems}
          isOpen={isBulkCreationOpen}
          onToggle={() => setIsBulkCreationOpen(prev => !prev)}
        />
      )}

      {/* Show asset upload progress */}
      {hasAssetUploads && (
        <ProgressCard
          type={UploadProgressType.ASSET_UPLOAD}
          items={assetUploadItems}
          isOpen={isAssetUploadOpen}
          onToggle={() => setIsAssetUploadOpen(prev => !prev)}
        />
      )}
    </div>
  );
};

export default UploadProgress;
